import { useState, useEffect, useMemo, useRef } from "react";
import {
  Box,
  Grid,
  Container,
  Typography,
  useTheme,
  Stack,
} from "@mui/material";
// components
import Page from "../components/Page";
import TimePeriodSelector from "../components/common/TimePeriodSelector";
import { DashboardCard } from "../components/_dashboard/app";
import {
  LiveChargingStatusMap,
  StationUptimeDashboard,
} from "../components/_dashboard/app";
import RevenueTrends from "../components/_dashboard/app/RevenueTrends";
import EnergyConsumption from "../components/_dashboard/app/EnergyConsumption";
import ChargingSessions from "../components/_dashboard/app/ChargingSessions";
import EnvironmentalImpact from "../components/_dashboard/app/EnvironmentalImpact";
import { useAuth } from "src/hooks/useAuth.hook";
import { dashboardService } from "src/services";
import { useSelectedTeam } from "src/hooks/useSelectedTeam";
import { useList } from "src/hooks/useFetch.hook";
import { useTransactionsWithDateRange } from "src/hooks/useTransactionsWithDateRange.hook";
import useTeamCarbonConfig from "src/hooks/useTeamCarbonConfig.hook";
import Map from "src/common/components/Map";
import {
  DashboardBarGraphStatsCard,
  DashboardDonutStatsCard,
  DashboardNumberStatsCard,
  DashboardMapCard,
} from "src/components/_dashboard/app/DashboardCard";
import {
  STATISTICS_URL,
  addDuplicatesValues,
  dashboardCards,
  requiredDateFormatForGraphXaxis,
  statisticValueFormat,
} from "./dashboardConfig";
import {
  dashboardDateFormatter,
} from "src/utils/formatTime";
import { is } from "src/util/is";
import { Loader } from "src/components/Loader";
import { alertSlice } from "src/store/alert/alert.slice";
import { useDispatch } from "react-redux";
import enums from "src/util/enums";
import { isSameDay } from "date-fns";

const DashboardAppNew = () => {
  const { user } = useAuth();
  const { data: chargers, isFetching: chargersListFetching } =
    useList("chargers");
  const theme = useTheme();
  const { teamId } = useSelectedTeam();
  const { constants: carbonConstants } = useTeamCarbonConfig(teamId);



  const {
    actions: { setAlert },
  } = alertSlice;
  const dispatch = useDispatch();
  const [isFetchingStats, setIsFetchingStats] = useState(true);
  const [selectedTimePeriod, setSelectedTimePeriod] = useState('Last7Days');
  const [isTeamChanging, setIsTeamChanging] = useState(false);
  
  // Initialize time interval immediately
  const getInitialTimeInterval = () => {
    const now = new Date();
    let startDate, endDate;

    // Calculate 7 days back from today (including today)
    startDate = new Date(now);
    startDate.setDate(startDate.getDate() - 6); // 6 days back + today = 7 days
    startDate.setHours(0, 0, 0, 0);
    endDate = new Date(now);
    endDate.setHours(23, 59, 59, 999);

    const timeInterval = {
      start: startDate.toISOString(),
      end: endDate.toISOString(),
      type: 'Last7Days',
    };

    return timeInterval;
  };

  const [customTimeInterval, setCustomTimeInterval] = useState(getInitialTimeInterval());
  const [customSelectedTime, setCustomSelectedTime] = useState({});
  
  // Time period options for the selector
  const timePeriodOptions = [
    { value: 'Today', label: 'Today' },
    { value: 'Last7Days', label: 'Last 7 Days' },
    { value: 'Last30Days', label: 'Last 30 Days' },
    { value: 'Last90Days', label: 'Last 90 Days' }
  ];

  // Handle time period change
  const handleTimePeriodChange = (period) => {
    setSelectedTimePeriod(period);
    
    const now = new Date();
    let startDate, endDate;

    switch (period) {
      case 'Today':
        startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0);
        endDate = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59);
        break;
      case 'Last7Days':
        // Calculate 7 days back from today (including today)
        startDate = new Date(now);
        startDate.setDate(startDate.getDate() - 6); // 6 days back + today = 7 days
        startDate.setHours(0, 0, 0, 0);
        endDate = new Date(now);
        endDate.setHours(23, 59, 59, 999);
        break;
      case 'Last30Days':
        // Calculate 30 days back from today (including today)
        startDate = new Date(now);
        startDate.setDate(startDate.getDate() - 29); // 29 days back + today = 30 days
        startDate.setHours(0, 0, 0, 0);
        endDate = new Date(now);
        endDate.setHours(23, 59, 59, 999);
        break;
      case 'Last90Days':
        // Calculate 90 days back from today (including today)
        startDate = new Date(now);
        startDate.setDate(startDate.getDate() - 89); // 89 days back + today = 90 days
        startDate.setHours(0, 0, 0, 0);
        endDate = new Date(now);
        endDate.setHours(23, 59, 59, 999);
        break;
      default:
        startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0);
        endDate = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59);
    }

    setCustomTimeInterval({
      start: startDate.toISOString(),
      end: endDate.toISOString(),
      type: period,
    });
  };

  // Initialize time interval on mount (now handled by useState initialization)
  useEffect(() => {
    // Component mounted
  }, []); // Only run on mount
  
  // Hook to fetch transactions when date range changes
  const { data: transactionsData, isFetching: transactionsFetching } = useTransactionsWithDateRange({
    teamId,
    timeInterval: customTimeInterval,
    enabled: !is.empty(customTimeInterval.start) && !is.empty(customTimeInterval.end) && !is.empty(customTimeInterval.type)
  });


  const [statsCards, setStatsCards] = useState(() => {
    const cardsArr = [...dashboardCards.get("statisticCards")].map((card) => ({
      ...card,
      bgColor: theme.palette[card.colorTheme][card.bgColor],
      fontColor: theme.palette[card.colorTheme][card.fontColor],
      colorTheme: theme.palette[card.colorTheme],
    }));
    return cardsArr;
  });

  const [donutStatsCard, setDonutStatsCard] = useState(() => {
    const statsArr = [...dashboardCards.get("donutStatsCards")].map((card) => ({
      ...card,
      svgColor: theme.palette[card.colorTheme][card.svgColor],
      fontColor: theme.palette[card.colorTheme][card.fontColor],
      colorTheme: theme.palette[card.colorTheme],
    }));
    return statsArr;
  });

  const [barStatsCard, setBarStatsCard] = useState({
    xaxis: [],
    yaxis: [],
  });


  // Map dashboard time intervals to chart period format
  const getChartPeriod = (timeInterval) => {
    if (!timeInterval || !timeInterval.type) return '7d';
    
    switch (timeInterval.type) {
      case 'Today':
        return '7d'; // Today maps to 7d for better visualization
      case 'Last7Days':
        return '7d';
      case 'Last30Days':
        return '30d';
      case 'Last90Days':
        return '90d';
      case 'Custom':
        // For custom ranges, determine the best period based on duration
        if (timeInterval.start && timeInterval.end) {
          const start = new Date(timeInterval.start);
          const end = new Date(timeInterval.end);
          const diffDays = Math.ceil((end - start) / (1000 * 60 * 60 * 24));
          
          if (diffDays <= 7) return '7d';
          if (diffDays <= 30) return '30d';
          return '90d';
        }
        return '7d';
      default:
        return '7d';
    }
  };

  useEffect(() => {
    (async function () {
      try {
        if (
          !is.empty(customTimeInterval.start) &&
          !is.empty(customTimeInterval.end) &&
          !is.empty(customTimeInterval.type)
        ) {
          const getDashboardStatistics = async () => {
            const fetchPromisesArr = [
              ...statsCards,
            ].map(({ url, field }) =>
              dashboardService.getIndividualStatistics(
                teamId,
                url,
                customTimeInterval,
                field
              )
            );

            Promise.all(fetchPromisesArr)
              .then((responses) => {
                const formattedResponse = responses.reduce(
                  (formatted, response) => {
                    let formattedResponse = { ...response };
                    delete formattedResponse.field;
                    formatted[response.field] = formattedResponse;
                    return formatted;
                  },
                  {}
                );
                if (!is.empty(formattedResponse)) {
                  const updatedStatsCards = statsCards.reduce(
                    (updatedStatsCards, statsCard) => {
                      let updatedCard = {
                        ...statsCard,
                        isFetching: false,
                        count: !is.nullOrUndefined(
                          formattedResponse[statsCard.field]?.count
                        )
                          ? statisticValueFormat(
                              "count",
                              formattedResponse[statsCard.field]?.count,
                              statsCard.field
                            )
                          : 0,
                        footer: !is.nullOrUndefined(
                          formattedResponse[statsCard.field]?.footer
                        )
                          ? statisticValueFormat(
                              "footer",
                              formattedResponse[statsCard.field]?.footer
                            )
                          : "",
                        percentChange: !is.nullOrUndefined(
                          formattedResponse[statsCard.field]?.percentChange
                        )
                          ? statisticValueFormat(
                              "percentChange",
                              formattedResponse[statsCard.field]?.percentChange,
                              customTimeInterval.type
                            )
                          : null,
                        icon: !is.nullOrUndefined(
                          formattedResponse[statsCard.field]?.percentChange
                        )
                          ? statisticValueFormat(
                              "icon",
                              formattedResponse[statsCard.field]?.percentChange
                            )
                          : "",
                      };
                      updatedStatsCards = [...updatedStatsCards, updatedCard];
                      return updatedStatsCards;
                    },
                    []
                  );
                  const dateFormat =
                    requiredDateFormatForGraphXaxis(customTimeInterval);
                  
                  // Store original dates for comparison
                  const dataWithDates = formattedResponse[
                    "cumulativeEnergyUsage"
                  ].cumulativeEnergyUsage.map(({ energyConsumption, chargingEnd }) => ({
                      energyConsumption,
                      chargingEnd: new Date(chargingEnd),
                      formattedDate: dashboardDateFormatter(
                        new Date(chargingEnd),
                        dateFormat
                      )
                    }));
                  
                  // Remove the first entry if it's from today
                  const today = new Date();
                  const filteredData = dataWithDates.length > 0 && isSameDay(dataWithDates[0].chargingEnd, today)
                    ? dataWithDates.slice(1)
                    : dataWithDates;
                  
                  const updatedBarStatsCard = filteredData.reduce(
                    (acc, { energyConsumption, formattedDate }) => {
                      acc.xaxis.push(formattedDate);
                      acc.yaxis.push(energyConsumption);
                      return acc;
                    },
                    { xaxis: [], yaxis: [] }
                  );
                  
                  const formattedStats =
                    addDuplicatesValues(updatedBarStatsCard);
                  setStatsCards(updatedStatsCards);
                  setBarStatsCard(formattedStats);
                  // setCustomTimeInterval({});
                }
              })
              .catch((error) => {
              });
          };
          if (customTimeInterval.type === "Custom") {
            const updatedStats = statsCards.map((card) => ({
              ...card,
              isFetching: true,
            }));
            setStatsCards(updatedStats);
            await getDashboardStatistics();
          } else {
            const updatedStats = statsCards.map((card) => ({
              ...card,
              isFetching: true,
            }));
            setStatsCards(updatedStats);
            await getDashboardStatistics();
          }
        }
      } catch (error) {
        console.error(error.response);
      }
    })();
  }, [customTimeInterval]);

  const processedChargersRef = useRef(new Set());
  
  useEffect(() => {
    if (!chargersListFetching && chargers && chargers.length > 0) {
      // Create a unique key for this chargers data to avoid reprocessing
      const chargersKey = chargers.map(c => `${c.id}-${c.status}`).join('|');
      
      // Only process if we haven't processed this exact data before
      if (!processedChargersRef.current.has(chargersKey)) {
        processedChargersRef.current.add(chargersKey);
        
        const chargerStats = chargers.reduce(
          (chargerStats, charger) => {
            // const currentDateTime = new Date();
            // const dateToCompare = new Date(charger.lastHeartbeatTimestamp);
            // const isBothDateSame = isSameDay(currentDateTime, dateToCompare);
            // if (isBothDateSame) {
            //   const isWithinTimeLimit = withinTimeBounds(
            //     dateToCompare,
            //     currentDateTime,
            //     15
            //   );
            //   if (isWithinTimeLimit)
            //     chargerStats = {
            //       ...chargerStats,
            //       totalChargers: chargerStats.totalChargers + 1,
            //       activeChargers: chargerStats.activeChargers + 1,
            //     };
            // } else
            if(charger.status==="Active"){
              chargerStats = {
                      ...chargerStats,
                      totalChargers: chargerStats.totalChargers + 1,
                      activeChargers: chargerStats.activeChargers + 1,
                    };
            }else
              chargerStats = {
                ...chargerStats,
                totalChargers: chargerStats.totalChargers + 1,
                inActiveChargers: chargerStats.inActiveChargers + 1,
              };
            return chargerStats;
          },
          { totalChargers: 0, activeChargers: 0, inActiveChargers: 0 }
        );
        
        setDonutStatsCard(prevCards => 
          prevCards.map(statsCard => ({
            ...statsCard,
            isFetching: false,
            count: chargerStats[statsCard.field] ?? 0,
          }))
        );
      }
    }
  }, [chargersListFetching, chargers]);

      useEffect(() => {
      // When team changes, reset to Last7Days instead of Today
      setIsTeamChanging(true);
      
      const now = new Date();
      const startDate = new Date(now);
      startDate.setDate(startDate.getDate() - 6); // 6 days back + today = 7 days
      startDate.setHours(0, 0, 0, 0);
      const endDate = new Date(now);
      endDate.setHours(23, 59, 59, 999);
      
      setCustomTimeInterval({
        start: startDate.toISOString(),
        end: endDate.toISOString(),
        type: "Last7Days",
      });
      setSelectedTimePeriod("Last7Days");
      setDonutStatsCard((donutStatsCard) =>
        donutStatsCard.map((stats) => ({ ...stats, isFetching: true }))
      );
      
      // Reset team changing state after a short delay to allow data to load
      setTimeout(() => {
        setIsTeamChanging(false);
      }, 1000);
    }, [teamId]);

  // Handle transactions data when it changes
  useEffect(() => {
    if (transactionsData && !transactionsFetching) {
      // You can add any logic here to handle the transactions data
      // For example, updating state, triggering other API calls, etc.
    }
  }, [transactionsData, transactionsFetching, customTimeInterval]);

  return (
    <Page title="Dashboard">
      <Container
        maxWidth="xl"
        sx={{ "@media screen and (min-width:600px)": { padding: 0 } }}
      >
        {isTeamChanging && (
          <Box
            sx={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(255, 255, 255, 0.8)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 9999,
            }}
          >
            <Loader />
          </Box>
        )}
        <Box sx={{ pb: { xs: 1, sm: 2 } }}>
            <Box sx={{ 
              display: 'flex', 
              flexDirection: { xs: 'column', sm: 'row' },
              justifyContent: 'space-between', 
              alignItems: { xs: 'flex-start', sm: 'center' }, 
              mb: 1,
              gap: { xs: 1, sm: 0 }
            }}>
            <Typography 
              variant="h5" 
              sx={{ 
                color: 'text.secondary', 
                fontWeight: 500,
                fontSize: { xs: '1rem', sm: '1.1rem', md: '1.25rem' }
              }}
            >
            InsightHub 🔎 – AI Analytics.
            </Typography>
            <TimePeriodSelector
              value={selectedTimePeriod}
              onChange={handleTimePeriodChange}
              options={timePeriodOptions}
            />
          </Box>
        </Box>
        
        {/* Revenue Trends, Energy Consumption, and Charging Sessions - Three Components in First Row */}
        <Grid container spacing={{ xs: 1.5, sm: 2, md: 2 }} sx={{ mb: { xs: 2, sm: 3, md: 3 } }}>
          <Grid item xs={12} sm={12} md={6} lg={4}>
            <RevenueTrends 
              key={`revenue-${teamId}-${customTimeInterval?.type}`}
              timeInterval={customTimeInterval} 
              transactionsData={transactionsData}
              isLoading={transactionsFetching || isTeamChanging}
              error={null}
            />
          </Grid>
          <Grid item xs={12} sm={12} md={6} lg={4}>
            <EnergyConsumption 
              key={`energy-${teamId}-${customTimeInterval?.type}`}
              timeInterval={customTimeInterval} 
              transactionsData={transactionsData}
              isLoading={transactionsFetching || isTeamChanging}
              error={null}
            />
          </Grid>
          <Grid item xs={12} sm={12} md={12} lg={4}>
            <ChargingSessions 
              key={`sessions-${teamId}-${customTimeInterval?.type}`}
              timeInterval={customTimeInterval} 
              transactionsData={transactionsData}
              isLoading={transactionsFetching || isTeamChanging}
              error={null}
            />
          </Grid>
        </Grid>

        {/* Environmental Impact and Station Uptime - Second Row */}
        <Grid container spacing={{ xs: 1.5, sm: 2, md: 2 }} sx={{ mb: { xs: 2, sm: 3, md: 3 } }}>
          <Grid item xs={12} sm={12} md={12} lg={6}>
            <EnvironmentalImpact
              key={`environmental-${teamId}-${customTimeInterval?.type}`}
              timeInterval={customTimeInterval}
              transactionsData={transactionsData}
              isLoading={transactionsFetching || isTeamChanging}
              carbonConstants={carbonConstants}
            />
          </Grid>
          <Grid item xs={12} sm={12} md={12} lg={6}>
            <StationUptimeDashboard 
              key={`uptime-${teamId}-${customTimeInterval?.type}`}
              timeRange={customTimeInterval}
            />
          </Grid>
        </Grid>

        {/* Charging Station Status and Geographic Overview - Third Row */}
        <Grid container spacing={{ xs: 1.5, sm: 2, md: 2 }} sx={{ mb: { xs: 2, sm: 3, md: 3 } }}>
          <Grid item xs={12} sm={12} md={12} lg={6}>
            <LiveChargingStatusMap />
          </Grid>
          <Grid item xs={12} sm={12} md={12} lg={6}>
            <DashboardMapCard
              title="Charging Station Locations"
              bgColor={theme.palette.secondary.lighter}
              fontColor={theme.palette.grey.A700}
            >
              {chargers && chargers.length > 0 ? (
                <Map searchBar={false} data={chargers} showMessage={false} />
              ) : (
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center', 
                  height: { xs: '300px', sm: '350px', md: '400px' },
                  color: 'text.secondary'
                }}>
                  {chargersListFetching ? 'Loading chargers...' : 'No chargers found'}
                </Box>
              )}
            </DashboardMapCard>
          </Grid>
        </Grid>
      </Container>
    </Page>
  );
};

export default DashboardAppNew;
